import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputBase,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FitnessCenter as FitnessIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";

import {StatusChip, FilterDropdown, Pagination} from "../componenets/ui";
import CompanyChips from "../componenets/administrators/CompanyChips";
import SortableTableCell from "../componenets/SortableTableCell";
import {getTemplates, getTemplateStats, createTemplate} from "../services/TemplatesService";
import {experienceLevels} from "../enums/ExperienceLevels";
import {bodyParts} from "../enums/BodyParts";
import {SecondaryMuscles} from "../enums/SecondaryMuscles";
import {TemplateGoals} from "../enums/TemplateGoals";
import CreateTemplateDialog from "../componenets/CreateTemplateDialog";
import {useAuth} from "../contexts/AuthContext";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";

/* ── Helpers ──────────────────────────────────────────────── */
const fmt = (ts) => ts ? moment.unix(ts).format("DD/MM/YY") : "—";

const goalLabel = (goals) => {
  if (!goals) return null;
  const arr = Array.isArray(goals) ? goals : [...(goals || [])];
  if (arr.length === 0) return null;
  return TemplateGoals[arr[0]] || null;
};

const expLabel = (val) => experienceLevels.find(l => l.value === val)?.name || "—";
const bodyPartLabel = (val) => bodyParts.find(b => b.value === val)?.name || "—";
const secondaryLabel = (val) => SecondaryMuscles.find(m => m.value === val)?.name || "—";

const GENDER_OPTS = [
  {value: "ALL", label: "הכול"},
  {value: "MALE", label: "זכר"},
  {value: "FEMALE", label: "נקבה"},
];

const EXP_OPTS = [
  {value: "ALL", label: "הכול"},
  ...experienceLevels.map(l => ({value: l.value, label: l.name})),
];

const WORKOUT_OPTS = [
  {value: 0, label: "הכול"},
  ...[1, 2, 3, 4, 5, 6, 7].map(n => ({value: n, label: `${n} ${n === 1 ? "אימון" : "אימונים"}`})),
];

const BODY_PART_OPTS = [
  {value: "ALL", label: "הכול"},
  ...bodyParts.map(b => ({value: b.value, label: b.name})),
];

/* ── Gender chip (recolored to coachTokens) ───────────────── */
const GenderChip = ({gender}) => {
  const isFemale = gender === "FEMALE";
  const isMale = gender === "MALE";
  return (
      <Chip
          label={isFemale ? "נ" : isMale ? "ז" : "—"}
          size="small"
          sx={{
            minWidth: 28, height: 22, fontSize: "11px", fontWeight: 700,
            bgcolor: isFemale ? t.redLight : isMale ? t.blueLight : t.bg,
            color: isFemale ? t.redText : isMale ? t.blueText : t.t3,
            "& .MuiChip-label": {px: "6px"},
          }}
      />
  );
};

/* ── Assigned-companies cell (group-admin list only) ───────── */
// A group admin's program is shared to a subset of their authorized companies. companyPermissions:
// null → all companies; [] → not assigned yet (CompanyChips renders "—"); [{companyId, companyName}]
// → those specific companies, shown as chips (with a "+N" overflow) — matching the administrators table.
const renderAssignedCompanies = (companyPermissions) => {
  if (companyPermissions === null) {
    return <Box component="span" sx={{fontSize: "11px", fontWeight: 600, color: t.greenText}}>כל החברות</Box>;
  }
  const companies = (Array.isArray(companyPermissions) ? companyPermissions : [])
      .map(c => ({id: c.companyId, name: c.companyName}));
  return <CompanyChips companies={companies} max={2}/>;
};

/* ── Table cell presets (match Clients) ───────────────────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
  background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const WORKOUT_BAR_ACCENTS = ["#6366F1", "#3B82F6", "#06B6D4", "#10B981", "#F59E0B", "#F97316", "#EF4444"];

/* ═══════════════════════════════════════════════════════════
   Templates page
   ═══════════════════════════════════════════════════════════ */
const NewTemplates = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const {isSuperAdmin, isCompanyGroupAdmin} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [f, setF] = useState({
    templates: [], total: 0,
    keyword: query.get("keyword") || "",
    gender: query.get("gender") || "ALL",
    experienceLevel: query.get("experienceLevel") || "ALL",
    numberOfWorkouts: query.get("numberOfWorkouts") ? +query.get("numberOfWorkouts") : 0,
    bodyPart: query.get("bodyPart") || "ALL",
    excludeSystem: query.get("excludeSystem") === "true",
    page: +(query.get("page") || 0),
    size: +(query.get("size") || 15),
    sortKey: query.get("sortKey") || "nameHeb",
    sortDir: query.get("sortDir") || "ASC",
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stats
  useEffect(() => {
    getTemplateStats(null).then(r => setStats(r.data)).catch(() => {});
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.gender !== "ALL") p.set("gender", ns.gender);
    if (ns.experienceLevel !== "ALL") p.set("experienceLevel", ns.experienceLevel);
    if (ns.numberOfWorkouts > 0) p.set("numberOfWorkouts", ns.numberOfWorkouts);
    if (ns.bodyPart !== "ALL") p.set("bodyPart", ns.bodyPart);
    if (ns.excludeSystem) p.set("excludeSystem", "true");
    if (ns.page > 0) p.set("page", ns.page);
    if (ns.size !== 15) p.set("size", ns.size);
    if (ns.sortKey && ns.sortKey !== "nameHeb") p.set("sortKey", ns.sortKey);
    if (ns.sortDir && ns.sortDir !== "ASC") p.set("sortDir", ns.sortDir);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  // Fetch
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getTemplates(
        f.page, f.size,
        f.numberOfWorkouts > 0 ? f.numberOfWorkouts : null,
        f.experienceLevel !== "ALL" ? f.experienceLevel : null,
        f.keyword || null,
        f.bodyPart !== "ALL" ? f.bodyPart : null,
        null, null, null, null,
        f.gender !== "ALL" ? f.gender : null,
        null,
        f.sortKey, f.sortDir,
        f.excludeSystem
    )
        .then(r => setF(p => ({...p, templates: r.data.content, total: r.data.totalElements, loading: false})))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.size, f.keyword, f.gender, f.experienceLevel, f.numberOfWorkouts, f.bodyPart, f.sortKey, f.sortDir, f.excludeSystem]);

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };
  const onSort = (field, dir) => set({sortKey: field, sortDir: dir});

  const handleSave = (template) => {
    setSaveError(null);
    createTemplate(template)
        .then((res) => {
          setIsCreateDialogOpen(false);
          navigate(`/app/templates/${res.data.id}`);
        })
        .catch((error) => {
          // Don't fail silently: a swallowed error here looks like "I created a program but nothing
          // happened". Keep the dialog open so the user can retry, and surface a message.
          console.error("Error saving template:", error);
          setSaveError("אירעה שגיאה ביצירת התוכנית. נסה שוב.");
        });
  };

  const pages = Math.ceil(f.total / f.size);

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* ─── Title row (no header band — sits on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              תוכניות אימון
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול תבניות תוכניות אימון</Typography>
          </Box>
          <Box component="button" onClick={() => setIsCreateDialogOpen(true)} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            תוכנית חדשה
          </Box>
        </Box>

        {/* ─── KPI grid (single card — total) ─────────────── */}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(4, 1fr)"}, gap: "14px", flexShrink: 0}}>
          <KPI
              icon={<FitnessIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.total?.toLocaleString() ?? "—"} valueColor={t.green}
              label="סה״כ תוכניות"
          />
        </Box>

        {/* ─── Workouts-per-week strip ─────────────────────── */}
        <Box sx={{
          display: "flex", gap: 0,
          bgcolor: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden", flexShrink: 0,
        }}>
          {!stats
              ? <Box sx={{flex: 1, display: "flex", justifyContent: "center", py: 1.5}}><CircularProgress size={18} sx={{color: t.green}}/></Box>
              : [1, 2, 3, 4, 5, 6, 7].map((day, i) => {
                  const count = stats.workoutsPerWeekBreakdown?.[day] || 0;
                  const dayLabel = `${day} אימונים בשבוע`;
                  const isActive = f.numberOfWorkouts === day;
                  const accent = WORKOUT_BAR_ACCENTS[i];
                  return (
                      <Box key={day} onClick={() => set({numberOfWorkouts: isActive ? 0 : day})} sx={{
                        flex: 1, textAlign: "center",
                        borderLeft: i > 0 ? `1px solid ${t.border}` : "none",
                        py: 1.5, px: 0.5,
                        cursor: "pointer",
                        bgcolor: isActive ? `${accent}12` : "transparent",
                        borderBottom: isActive ? `2px solid ${accent}` : "2px solid transparent",
                        transition: "all 0.15s",
                        "&:hover": {bgcolor: `${accent}08`},
                      }}>
                        <Typography sx={{fontSize: "18px", fontWeight: 700, color: count > 0 ? accent : t.t3, lineHeight: 1.2}}>
                          {count}
                        </Typography>
                        <Typography sx={{fontSize: "10px", fontWeight: 500, color: isActive ? accent : t.t3, mt: 0.25}}>
                          {dayLabel}
                        </Typography>
                      </Box>
                  );
                })
          }
        </Box>

        {/* ─── Table card (matches Clients) ───────────────── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0,
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <FitnessIcon sx={{fontSize: 14}}/>
              {f.total} תוכניות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם תוכנית..."
                  sx={{
                    width: 220,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            <FilterDropdown label="מגדר" value={f.gender} onChange={v => set({gender: v})} options={GENDER_OPTS} minWidth={90}/>
            <FilterDropdown label="רמה" value={f.experienceLevel} onChange={v => set({experienceLevel: v})} options={EXP_OPTS} minWidth={100}/>
            <FilterDropdown label="אימונים בשבוע" value={f.numberOfWorkouts} onChange={v => set({numberOfWorkouts: v})} options={WORKOUT_OPTS} minWidth={130}/>
            <FilterDropdown label="דגש ראשי" value={f.bodyPart} onChange={v => set({bodyPart: v})} options={BODY_PART_OPTS} minWidth={110}/>

            {/* "My templates only" toggle — hides system templates. Meaningless for a super-admin,
                whose Templates view IS the system templates (routed to /system/template). */}
            {!isSuperAdmin() && (
              <Box
                  onClick={() => set({excludeSystem: !f.excludeSystem})}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    fontSize: "12px", fontWeight: 700, cursor: "pointer", userSelect: "none",
                    padding: "5px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                    transition: "all .15s",
                    color: f.excludeSystem ? t.white : t.t3,
                    background: f.excludeSystem ? t.green : t.white,
                    border: `1.5px solid ${f.excludeSystem ? t.green : t.border}`,
                  }}
              >
                התבניות שלי בלבד
              </Box>
            )}

            <Box sx={{flex: 1}}/>
          </Box>

          {/* Table body */}
          {f.loading && !f.templates.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 8}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.templates.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: "column", gap: 1}}>
                <Typography sx={{color: t.t2, fontSize: "15px", fontWeight: 500}}>לא נמצאו תוכניות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>נסה לשנות את הסינון</Typography>
              </Box>
          ) : (
              <>
                <TableContainer>
                  <Table sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell field="nameHeb" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "right", width: 220}}>שם תוכנית</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 55}}>מגדר</TableCell>
                        <TableCell sx={{...th, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>מבנה</TableCell>
                        {isCompanyGroupAdmin() && (
                            <TableCell sx={{...th, textAlign: "right", width: 210}}>חברות</TableCell>
                        )}
                        <SortableTableCell field="totalExercises" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "center", width: 75, display: {xs: "none", md: "table-cell"}}}>תרגילים</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 75}}>רמה</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 90, display: {xs: "none", md: "table-cell"}}}>דגש ראשי</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 90, display: {xs: "none", md: "table-cell"}}}>דגש משני</TableCell>
                        <SortableTableCell field="enabled" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "center", width: 80}}>סטטוס</SortableTableCell>
                        <SortableTableCell field="createdDate" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "center", width: 90}}>תאריך</SortableTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.templates.map(template => (
                          <TableRow key={template.id} onClick={() => navigate(`/app/templates/${template.id}`)}
                                    sx={{cursor: "pointer", bgcolor: t.white, "&:hover": {bgcolor: t.bg}, transition: "background 0.15s ease"}}>
                            {/* Name + Goal */}
                            <TableCell sx={{...td, textAlign: "right", overflow: "hidden"}}>
                              <Box sx={{overflow: "hidden"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden"}}>
                                  <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {template.nameHeb || template.name}
                                  </Typography>
                                  {template.ownerUserId && <StatusChip label="מנהל קבוצת חברות" variant="warning" size="small"/>}
                                </Box>
                                {goalLabel(template.goals) && (
                                    <Typography sx={{fontSize: "11px", color: t.t3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                      {goalLabel(template.goals)}
                                    </Typography>
                                )}
                              </Box>
                            </TableCell>
                            {/* Gender */}
                            <TableCell sx={{...td, textAlign: "center"}}>
                              <GenderChip gender={template.gender}/>
                            </TableCell>
                            {/* Structure */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              <Box sx={{overflow: "hidden"}}>
                                <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.text, whiteSpace: "nowrap"}}>
                                  {template.numOfWorkouts} אימונים בשבוע
                                </Typography>
                                {(template.numberOfBlocks || template.numberOfWeeksInBlock) && (
                                    <Typography sx={{fontSize: "11px", color: t.t3, whiteSpace: "nowrap"}}>
                                      {[
                                        template.numberOfBlocks ? `${template.numberOfBlocks} בלוקים` : null,
                                        template.numberOfWeeksInBlock ? `${template.numberOfWeeksInBlock} שבועות` : null,
                                      ].filter(Boolean).join(" · ")}
                                    </Typography>
                                )}
                              </Box>
                            </TableCell>
                            {/* Assigned companies (group admin only) */}
                            {isCompanyGroupAdmin() && (
                                <TableCell sx={{...td, textAlign: "right"}} onClick={e => e.stopPropagation()}>
                                  {renderAssignedCompanies(template.companyPermissions)}
                                </TableCell>
                            )}
                            {/* Exercises */}
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "13px", fontWeight: 500, color: template.totalExercises > 0 ? t.text : t.t3}}>
                                {template.totalExercises || "—"}
                              </Typography>
                            </TableCell>
                            {/* Experience */}
                            <TableCell sx={{...td, textAlign: "center"}}>
                              <Typography sx={{fontSize: "12px", fontWeight: 500, color: t.t2}}>
                                {expLabel(template.experienceLevel)}
                              </Typography>
                            </TableCell>
                            {/* Primary focus */}
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "12px", fontWeight: 500, color: t.t2}}>
                                {bodyPartLabel(template.bodyPart)}
                              </Typography>
                            </TableCell>
                            {/* Secondary */}
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "12px", fontWeight: 500, color: t.t3}}>
                                {template.secondaryMuscle ? secondaryLabel(template.secondaryMuscle) : "—"}
                              </Typography>
                            </TableCell>
                            {/* Status */}
                            <TableCell sx={{...td, textAlign: "center"}}>
                              {template.enabled
                                  ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                                  : <StatusChip label="לא פעיל" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                            </TableCell>
                            {/* Date */}
                            <TableCell sx={{...td, textAlign: "center", color: t.t2}}>
                              {fmt(template.createdDate)}
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={f.size}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}
                                onItemsPerPageChange={sz => set({size: sz})}/>
                )}
              </>
          )}
        </Box>

        <CreateTemplateDialog
            open={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            onSave={handleSave}
        />

        <Snackbar
            open={!!saveError}
            autoHideDuration={6000}
            onClose={() => setSaveError(null)}
            anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        >
          <Alert severity="error" onClose={() => setSaveError(null)} sx={{width: "100%"}}>
            {saveError}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default NewTemplates;
